import React, { createContext, useContext, useReducer, useCallback } from 'react';
import { getInitialTab } from '../constants/tabs';

// Action types
const ActionTypes = {
  SET_DATA_LOADING: 'SET_DATA_LOADING',
  SET_SEARCH_RESULTS: 'SET_SEARCH_RESULTS',
  SET_RECENT_PROPERTIES: 'SET_RECENT_PROPERTIES',
  SET_RECENT_TENANT_PROFILES: 'SET_RECENT_TENANT_PROFILES',
  SET_VERIFIED_TENANT_PROFILES: 'SET_VERIFIED_TENANT_PROFILES',
  SET_PREMIUM_TENANT_PROFILES: 'SET_PREMIUM_TENANT_PROFILES',
  SET_SELECTED_TAB: 'SET_SELECTED_TAB',
  SET_SEARCH_FILTERS: 'SET_SEARCH_FILTERS',
  SET_MODAL_STATE: 'SET_MODAL_STATE',
  SET_SELECTED_PROPERTY: 'SET_SELECTED_PROPERTY',
  SET_SELECTED_TENANT: 'SET_SELECTED_TENANT'
};

// Initial state
const initialState = {
  // Data states
  recentProperties: [],
  searchResults: null,
  recentTenantProfiles: [],
  verifiedTenantProfiles: [],
  premiumTenantProfiles: [],
  dataLoading: true,
  
  // UI states
  selectedTab: getInitialTab(),
  propertyDetailsOpen: false,
  selectedProperty: null,
  tenantDetailsOpen: false,
  selectedTenant: null,
  
  // Search filters
  searchFilters: {
    city: '',
    district: '',
    street: '',
    rooms: [],
    priceRange: { min: '', max: '' },
    sizeRange: { min: '', max: '' },
    amenities: [],
    maxPersons: null,
    dateAdded: null,
    smoking: 'nie',
    nonSmoking: false,
    noPets: false
  }
};

// Reducer function
const appReducer = (state, action) => {
  switch (action.type) {
    case ActionTypes.SET_DATA_LOADING:
      return { ...state, dataLoading: action.payload };
      
    case ActionTypes.SET_SEARCH_RESULTS:
      return { ...state, searchResults: action.payload };
      
    case ActionTypes.SET_RECENT_PROPERTIES:
      return { ...state, recentProperties: action.payload };
      
    case ActionTypes.SET_RECENT_TENANT_PROFILES:
      return { ...state, recentTenantProfiles: action.payload };
      
    case ActionTypes.SET_VERIFIED_TENANT_PROFILES:
      return { ...state, verifiedTenantProfiles: action.payload };
      
    case ActionTypes.SET_PREMIUM_TENANT_PROFILES:
      return { ...state, premiumTenantProfiles: action.payload };
      
    case ActionTypes.SET_SELECTED_TAB:
      return { ...state, selectedTab: action.payload };
      
    case ActionTypes.SET_SEARCH_FILTERS:
      return { 
        ...state, 
        searchFilters: typeof action.payload === 'function' 
          ? action.payload(state.searchFilters)
          : { ...state.searchFilters, ...action.payload }
      };
      
    case ActionTypes.SET_MODAL_STATE:
      return { ...state, ...action.payload };
      
    case ActionTypes.SET_SELECTED_PROPERTY:
      return { 
        ...state, 
        selectedProperty: action.payload,
        propertyDetailsOpen: action.payload !== null
      };
      
    case ActionTypes.SET_SELECTED_TENANT:
      return { 
        ...state, 
        selectedTenant: action.payload,
        tenantDetailsOpen: action.payload !== null
      };
      
    default:
      return state;
  }
};

// Create contexts
const AppContext = createContext();
const AppDispatchContext = createContext();

// Context provider component
export const AppProvider = ({ children }) => {
  const [state, dispatch] = useReducer(appReducer, initialState);

  // Action creators with useCallback for performance
  const actions = {
    setDataLoading: useCallback((loading) => {
      dispatch({ type: ActionTypes.SET_DATA_LOADING, payload: loading });
    }, []),

    setSearchResults: useCallback((results) => {
      dispatch({ type: ActionTypes.SET_SEARCH_RESULTS, payload: results });
    }, []),

    setRecentProperties: useCallback((properties) => {
      dispatch({ type: ActionTypes.SET_RECENT_PROPERTIES, payload: properties });
    }, []),

    setRecentTenantProfiles: useCallback((profiles) => {
      dispatch({ type: ActionTypes.SET_RECENT_TENANT_PROFILES, payload: profiles });
    }, []),

    setVerifiedTenantProfiles: useCallback((profiles) => {
      dispatch({ type: ActionTypes.SET_VERIFIED_TENANT_PROFILES, payload: profiles });
    }, []),

    setPremiumTenantProfiles: useCallback((profiles) => {
      dispatch({ type: ActionTypes.SET_PREMIUM_TENANT_PROFILES, payload: profiles });
    }, []),

    setSelectedTab: useCallback((tab) => {
      dispatch({ type: ActionTypes.SET_SELECTED_TAB, payload: tab });
    }, []),

    setSearchFilters: useCallback((filters) => {
      dispatch({ type: ActionTypes.SET_SEARCH_FILTERS, payload: filters });
    }, []),

    updateSearchFilter: useCallback((field, value) => {
      dispatch({ 
        type: ActionTypes.SET_SEARCH_FILTERS, 
        payload: (prevFilters) => {
          const newFilters = { ...prevFilters, [field]: value };
          
          // Reset street and district when city changes
          if (field === 'city') {
            newFilters.district = '';
            newFilters.street = '';
          }
          
          return newFilters;
        }
      });
    }, []),

    setModalState: useCallback((modalStates) => {
      dispatch({ type: ActionTypes.SET_MODAL_STATE, payload: modalStates });
    }, []),

    setSelectedProperty: useCallback((property) => {
      dispatch({ type: ActionTypes.SET_SELECTED_PROPERTY, payload: property });
    }, []),

    setSelectedTenant: useCallback((tenant) => {
      dispatch({ type: ActionTypes.SET_SELECTED_TENANT, payload: tenant });
    }, []),

    // Convenience method for handling property clicks
    handlePropertyClick: useCallback((property) => {
      dispatch({ type: ActionTypes.SET_SELECTED_PROPERTY, payload: property });
    }, []),

    // Convenience method for handling tenant clicks
    handleTenantClick: useCallback((tenant) => {
      dispatch({ type: ActionTypes.SET_SELECTED_TENANT, payload: tenant });
    }, []),

    // Close modals
    closePropertyDetails: useCallback(() => {
      dispatch({ type: ActionTypes.SET_SELECTED_PROPERTY, payload: null });
    }, []),

    closeTenantDetails: useCallback(() => {
      dispatch({ type: ActionTypes.SET_SELECTED_TENANT, payload: null });
    }, [])
  };

  return (
    <AppContext.Provider value={state}>
      <AppDispatchContext.Provider value={actions}>
        {children}
      </AppDispatchContext.Provider>
    </AppContext.Provider>
  );
};

// Custom hooks for using the contexts
export const useAppState = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useAppState must be used within AppProvider');
  }
  return context;
};

export const useAppActions = () => {
  const context = useContext(AppDispatchContext);
  if (!context) {
    throw new Error('useAppActions must be used within AppProvider');
  }
  return context;
};

// Combined hook for convenience
export const useApp = () => {
  return {
    state: useAppState(),
    actions: useAppActions()
  };
};